/**
 * API Keys Page (/admin/api-keys)
 *
 * Allows FI administrators to list, create, and revoke API keys for their
 * tenant. Keys are stored as metadata (hash only) on the institution
 * configuration row. The plain-text key is returned exactly once at creation.
 *
 * Backend: GET/POST/DELETE /api/admin/settings/api-keys
 * Types: components['schemas']['ApiKeyRecord'] / 'CreatedKeyResult'
 *        from platform-admin.openapi.yaml
 */

import { useState, useEffect, useCallback } from 'react';
import { Key, Plus, Trash2, Copy } from 'lucide-react';
import {
  Heading,
  Alert,
  AlertBanner,
  Button,
  Card,
  Badge,
  DataTable,
  type DataTableColumn,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { platformAdminClient } from '../../api/client';

// All types derived from generated spec -- no manual interfaces for API shapes
type ApiKeyRecord = components['schemas']['ApiKeyRecord'];
type CreatedKeyResult = components['schemas']['CreatedKeyResult'];

const EXPIRE_OPTIONS: Array<{ label: string; value: number | null }> = [
  { label: 'Never', value: null },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '180 days', value: 180 },
  { label: '365 days', value: 365 },
];

export function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createExpiry, setCreateExpiry] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<CreatedKeyResult | null>(null);
  const [copied, setCopied] = useState(false);

  // Delete confirmation state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await platformAdminClient.GET(
        '/settings/api-keys',
      );
      if (fetchError) throw new Error('Failed to load API keys');
      setKeys(data?.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async () => {
    if (!createName.trim()) {
      setCreateError('Key name is required');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const { data, error: fetchError } = await platformAdminClient.POST(
        '/settings/api-keys',
        {
          body: {
            name: createName.trim(),
            expiresInDays: createExpiry,
          },
        },
      );
      if (fetchError) throw new Error('Failed to create key');
      if (data?.key) {
        setNewKey(data.key);
      }
      setCreateName('');
      setCreateExpiry(null);
      loadKeys();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId: string) => {
    setDeletingId(keyId);
    try {
      const { error: fetchError } = await platformAdminClient.DELETE(
        '/settings/api-keys/{keyId}',
        { params: { path: { keyId } } },
      );
      if (fetchError) throw new Error('Failed to revoke key');
      loadKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyKey = () => {
    if (newKey?.plainTextKey) {
      navigator.clipboard.writeText(newKey.plainTextKey).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  // DataTable prints an accessor's value as text; markup goes in `cell`.
  // Each column here returned its markup from `accessor`, so a tenant with a
  // key saw "[object Object]" in every cell, the revoke control included.
  const columns: DataTableColumn<ApiKeyRecord>[] = [
    {
      header: 'Name',
      accessor: 'name',
      cell: (_value, row) => (
        <div className="flex items-center gap-2">
          <Key size={16} className="text-tertiary flex-shrink-0" />
          <span className="text-sm font-medium text-primary">{row.name}</span>
        </div>
      ),
    },
    {
      header: 'Prefix',
      accessor: 'keyPrefix',
      cell: (_value, row) => (
        <span className="text-xs font-mono text-secondary tracking-wider">
          {row.keyPrefix}...
        </span>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (_value, row) => (
        <Badge
          variant={row.status === 'ACTIVE' ? 'success' : 'error'}
          size="sm"
        >
          {row.status}
        </Badge>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      cell: (_value, row) => (
        <span className="text-sm text-secondary">
          {new Date(row.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      header: 'Last Used',
      accessor: 'lastUsedAt',
      cell: (_value, row) => (
        <span className="text-sm text-secondary">
          {row.lastUsedAt ? new Date(row.lastUsedAt).toLocaleDateString() : 'Never'}
        </span>
      ),
    },
    {
      header: 'Expires',
      accessor: 'expiresAt',
      cell: (_value, row) => (
        <span className="text-sm text-secondary">
          {row.expiresAt ? new Date(row.expiresAt).toLocaleDateString() : 'Never'}
        </span>
      ),
    },
    {
      header: 'Actions',
      align: 'right',
      accessor: 'id',
      cell: (_value, row) =>
        row.status === 'ACTIVE' ? (
          <button
            onClick={() => {
              if (window.confirm(`Revoke API key "${row.name}"? This cannot be undone.`)) {
                handleRevoke(row.id);
              }
            }}
            disabled={deletingId === row.id}
            className="p-2 text-error hover:text-error/80 disabled:opacity-50"
            title="Revoke key"
          >
            <Trash2 size={16} />
          </button>
        ) : (
          <span className="text-xs text-tertiary pr-2">
            {row.revokedAt ? new Date(row.revokedAt).toLocaleDateString() : 'Revoked'}
          </span>
        ),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeKeys = keys.filter((k) => k.status === 'ACTIVE');
  const revokedKeys = keys.filter((k) => k.status === 'REVOKED');

  return (
    <div>
      {/* Page Heading */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            Manage programmatic access credentials for your institution.
            Keys are shown once at creation -- store them securely.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setShowCreate(true);
            setNewKey(null);
            setCreateError(null);
          }}
        >
          <Plus size={16} />
          Create Key
        </Button>
      </div>

      {/* Global error */}
      {error && (
        <div className="mb-4">
          <AlertBanner type="error" message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      {/* Create Key Panel */}
      {showCreate && (
        <Card className="mb-6">
          <div className="px-6 py-4 border-b border-recessed">
            <Heading level={2} size="subsection">New API Key</Heading>
          </div>

          {/* Newly created key display */}
          {newKey ? (
            <div className="p-6">
              <div className="bg-growth/5 border border-growth/20 rounded-sm p-4 mb-4">
                <p className="text-sm font-sans font-medium text-growth mb-1">
                  Key created successfully. Copy it now -- it will not be shown again.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <code className="flex-1 px-3 py-2 bg-elevated border border-recessed rounded-sm text-sm font-mono text-primary break-all">
                  {newKey.plainTextKey}
                </code>
                <Button variant="secondary" onClick={handleCopyKey}>
                  <Copy size={16} />
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <div className="flex justify-end mt-4">
                <Button variant="ghost" onClick={() => { setShowCreate(false); setNewKey(null); }}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              {createError && (
                <Alert type="error">
                  {createError}
                </Alert>
              )}
              <div>
                <label className="block text-label-xs font-sans font-semibold uppercase text-tertiary mb-1">
                  Key Name
                </label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="e.g. Production Integration"
                  className="w-full px-3 py-2 border border-recessed rounded-sm text-sm font-sans text-primary placeholder:text-tertiary focus:outline-none focus:border-accent"
                />
              </div>
              <div>
                <label className="block text-label-xs font-sans font-semibold uppercase text-tertiary mb-1">
                  Expiration
                </label>
                <select
                  value={createExpiry ?? ''}
                  onChange={(e) =>
                    setCreateExpiry(e.target.value ? parseInt(e.target.value, 10) : null)
                  }
                  className="w-full px-3 py-2 border border-recessed rounded-sm text-sm font-sans text-primary focus:outline-none focus:border-accent"
                >
                  {EXPIRE_OPTIONS.map((opt) => (
                    <option key={String(opt.value)} value={opt.value ?? ''}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => { setShowCreate(false); setCreateError(null); }}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleCreate} disabled={creating}>
                  {creating ? 'Creating...' : 'Create Key'}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Active Keys */}
      <Card className="mb-6">
        <div className="px-6 py-4 border-b border-recessed flex items-center justify-between">
          <Heading level={2} size="subsection">Active Keys</Heading>
          <Badge variant="info" size="sm">{activeKeys.length}</Badge>
        </div>
        <div className="p-4">
          {activeKeys.length === 0 ? (
            <div className="text-center py-8 text-sm text-tertiary">
              No active API keys. Create one to enable programmatic access.
            </div>
          ) : (
            <DataTable columns={columns} data={activeKeys} hoverable striped />
          )}
        </div>
      </Card>

      {/* Revoked Keys */}
      {revokedKeys.length > 0 && (
        <Card>
          <div className="px-6 py-4 border-b border-recessed flex items-center justify-between">
            <Heading level={2} size="subsection">Revoked Keys</Heading>
            <Badge variant="neutral" size="sm">{revokedKeys.length}</Badge>
          </div>
          <div className="p-4">
            <DataTable columns={columns} data={revokedKeys} hoverable />
          </div>
        </Card>
      )}
    </div>
  );
}
